/**
 * A numbered vertical stepper for the evaluator's definition: a circled
 * number per step joined by a thin line, a header that folds the step away,
 * and a one-line hint. A finished step's number turns into a check, and a
 * folded step says in a few words what it holds ("Labels · 3 options"). A
 * step that needs a look (a field not in the case) wears a warning dot.
 */

import { useId } from "react";
import { Check, ChevronDown } from "lucide-react";

export function Steps({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <ol aria-label={label} className="m-0 flex list-none flex-col p-0">
      {children}
    </ol>
  );
}

export function Step({
  n,
  title,
  hint,
  done,
  warn,
  summary,
  open,
  onToggle,
  last,
  children,
}: {
  n: number;
  title: string;
  hint: string;
  done: boolean;
  warn?: boolean;
  /** Shown in place of the hint while the step is folded. */
  summary: string;
  open: boolean;
  onToggle: () => void;
  last?: boolean;
  children: React.ReactNode;
}) {
  const body = useId();
  return (
    <li className="grid grid-cols-[1.75rem_minmax(0,1fr)] gap-x-3 max-sm:gap-x-2.5">
      <div className="flex flex-col items-center">
        <span
          className={`relative mt-0.5 flex size-7 shrink-0 items-center justify-center rounded-full border text-xs font-semibold tabular-nums transition-colors ${
            done ? "border-success bg-success-bg text-success" : "border-line bg-surface text-muted"
          }`}
        >
          {done ? <Check aria-hidden className="size-3.5" strokeWidth={2.5} /> : n}
          {warn && (
            <span
              aria-hidden
              className="absolute -top-0.5 -right-0.5 size-2.5 rounded-full border-2 border-surface bg-warn"
            />
          )}
        </span>
        {!last && <span aria-hidden className="mt-1 w-px flex-1 bg-line max-sm:hidden" />}
      </div>
      <div className={`flex min-w-0 flex-col ${last ? "" : "pb-6"}`}>
        <h2 className="m-0">
          <button
            type="button"
            aria-expanded={open}
            aria-controls={body}
            onClick={onToggle}
            className="group flex w-full items-start gap-2 rounded-md py-1.5 text-left outline-none focus-visible:ring-2 focus-visible:ring-focus"
          >
            <ChevronDown
              aria-hidden
              className={`mt-0.5 size-4 shrink-0 text-faint motion-safe:transition-transform group-hover:text-muted ${
                open ? "" : "-rotate-90"
              }`}
            />
            <span className="flex min-w-0 flex-1 flex-col gap-0.5">
              <span className="flex flex-wrap items-baseline gap-x-2 text-sm font-semibold text-ink">
                {title}
                <span className="sr-only">{done ? "(done)" : warn ? "(needs a look)" : "(to do)"}</span>
              </span>
              <span className={`text-xs ${open ? "text-muted" : "text-body"}`}>{open ? hint : summary}</span>
            </span>
          </button>
        </h2>
        {/* On a phone the step body takes the full width, under the number too. */}
        <div id={body} hidden={!open} className="pt-3 max-sm:-ml-[2.375rem]">
          {children}
        </div>
      </div>
    </li>
  );
}
